import {datadogRum} from '@datadog/browser-rum';

import {assert} from 'Asserts';
import {measureTimingBetweenEvents} from 'performance/BenchlingProfiler';

import {ProfilerEvent, ProfilerEventDomain} from './ProfilerConstants';

/* Should bump version anytime there's a change to logging events or processing them,
 * so that on Datadog, we can quickly see which custom actions have outdated logic or tags
 */
const PROFILER_NAVIGATION_EVENTS_VERSION = 1;
function addDatadogAction(name: string, context: object): void {
  datadogRum.addAction(name, {...context, versionTag: PROFILER_NAVIGATION_EVENTS_VERSION});
}

export enum ProfilerNavigationFeatures {
  RELEVANT_TEMPLATES_SEARCH = 'relevantTemplatesSearch',
  FILE_STATES = 'fileStates',
}

const FEATURE_TO_PROCESSING_FUNCTION: {[key in ProfilerNavigationFeatures]: Function} = {
  [ProfilerNavigationFeatures.RELEVANT_TEMPLATES_SEARCH]: processRelevantTemplatesSearch,
  [ProfilerNavigationFeatures.FILE_STATES]: processFileStateOpening,
};

enum ProfilerNavigationActions {
  SEARCH_STARTED = 'searchStarted',
  SEARCH_RESULTS_RENDERED = 'searchResultsRendered',
  FILE_STATE_LOADING = 'fileStateLoading',
  FILE_STATE_INITIALLY_LOADED_EVENT = 'fileStateInitiallyLoaded',
}

export const FILE_STATE_LOADING_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NAVIGATION,
  feature: ProfilerNavigationFeatures.FILE_STATES,
  action: ProfilerNavigationActions.FILE_STATE_LOADING,
};

// Tracks when the file has been fetched and can start rendering. We may
// want to add a "fully loaded" event at some point to track when the tabs
// have fetched data and rendered.
export const FILE_STATE_INITIALLY_LOADED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NAVIGATION,
  feature: ProfilerNavigationFeatures.FILE_STATES,
  action: ProfilerNavigationActions.FILE_STATE_INITIALLY_LOADED_EVENT,
};

export const RELEVANT_TEMPLATES_SEARCH_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NAVIGATION,
  feature: ProfilerNavigationFeatures.RELEVANT_TEMPLATES_SEARCH,
  action: ProfilerNavigationActions.SEARCH_STARTED,
};
export const RELEVANT_TEMPLATES_SEARCH_RESULTS_RENDERED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NAVIGATION,
  feature: ProfilerNavigationFeatures.RELEVANT_TEMPLATES_SEARCH,
  action: ProfilerNavigationActions.SEARCH_RESULTS_RENDERED,
};

export function processNavigationEvents(profilerEvent: Pick<ProfilerEvent, 'feature' | 'action'>): void {
  const feature = profilerEvent.feature as ProfilerNavigationFeatures;
  assert(
    Object.values(ProfilerNavigationFeatures).includes(feature),
    `Unexpected profiler feature: ${profilerEvent.feature}`
  );
  FEATURE_TO_PROCESSING_FUNCTION[feature]();
}

/**
 * Logs the time from when a file state was originally opened to when the file
 * query has finished and the file can begin rendering. Note that subtabs may still need
 * to fetch data before rendering, so this is just counting the time for the initial
 * file load event and may be lower than the user-perceived time to load the file.
 */
function processFileStateOpening(): void {
  const measurement = measureTimingBetweenEvents(FILE_STATE_LOADING_EVENT, FILE_STATE_INITIALLY_LOADED_EVENT);
  if (measurement == null) {
    return;
  }

  const {time, startEvent: fileStateLoading, endEvent: fileStateLoaded} = measurement;

  if (fileStateLoading.extra?.fileId !== fileStateLoaded.extra?.fileId) {
    // Exit early if the fileIds don't match, since the previous file must not have cleaned up
    // properly.
    return;
  }

  addDatadogAction('FileState Initially Loaded', {
    time,
    fileId: fileStateLoading.extra?.fileId,
    fileType: fileStateLoading.extra?.fileType,
    // isFirstLoad captures whether this is the first file to be opened in the workspace.
    // This is most often on page load, though may also occur if the user closes all tabs
    // and then opens up a new file state. The goal of this is to capture pageLoad events,
    // but this is easier to compute and almost always a page load event.
    isFirstLoad: fileStateLoading.extra?.isFirstLoad,
  });
}

function processRelevantTemplatesSearch(): void {
  const measurement = measureTimingBetweenEvents(
    RELEVANT_TEMPLATES_SEARCH_STARTED_EVENT,
    RELEVANT_TEMPLATES_SEARCH_RESULTS_RENDERED_EVENT
  );
  if (measurement == null) {
    return;
  }

  const {time, startEvent: searchStarted, endEvent: searchResultsRendered} = measurement;

  const hasSearchTerm = searchStarted.extra?.hasSearchTerm;
  const hasCollectionFilter = searchStarted.extra?.hasCollectionFilter;
  const totalPostFiltering = searchResultsRendered.extra?.totalPostFiltering;
  const totalInaccessible = searchResultsRendered.extra?.totalInaccessible;
  const totalAccessibleCollections = searchResultsRendered.extra?.totalAccessibleCollections;

  addDatadogAction('Relevant Templates Listing: Start Search to Results Rendered', {
    time,
    hasSearchTerm,
    hasCollectionFilter,
    totalPostFiltering,
    totalInaccessible,
    totalAccessibleCollections,
  });
}
